import { useState } from "react";
import { useTranslation } from "react-i18next";

// Color Select Component with preview
export const ColorSelect: React.FC<{
  value?: string;
  onChange: (color: string) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const colors = [
    { name: 'Slate', value: 'slate', color: '#64748b' },
    { name: 'Gray', value: 'gray', color: '#6b7280' },
    { name: 'Zinc', value: 'zinc', color: '#71717a' },
    { name: 'Neutral', value: 'neutral', color: '#737373' },
    { name: 'Stone', value: 'stone', color: '#78716c' },
    { name: 'Red', value: 'red', color: '#ef4444' },
    { name: 'Orange', value: 'orange', color: '#f97316' },
    { name: 'Amber', value: 'amber', color: '#f59e0b' },
    { name: 'Yellow', value: 'yellow', color: '#eab308' },
    { name: 'Lime', value: 'lime', color: '#84cc16' },
    { name: 'Green', value: 'green', color: '#22c55e' },
    { name: 'Emerald', value: 'emerald', color: '#10b981' },
    { name: 'Teal', value: 'teal', color: '#14b8a6' },
    { name: 'Cyan', value: 'cyan', color: '#06b6d4' },
    { name: 'Sky', value: 'sky', color: '#0ea5e9' },
    { name: 'Blue', value: 'blue', color: '#3b82f6' },
    { name: 'Indigo', value: 'indigo', color: '#6366f1' },
    { name: 'Violet', value: 'violet', color: '#8b5cf6' },
    { name: 'Purple', value: 'purple', color: '#a855f7' },
    { name: 'Fuchsia', value: 'fuchsia', color: '#d946ef' },
    { name: 'Pink', value: 'pink', color: '#ec4899' },
    { name: 'Rose', value: 'rose', color: '#f43f5e' }
  ];

  const selectedColor = colors.find(c => c.value === value);
  const filteredColors = colors.filter(color => 
    color.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (color: typeof colors[0]) => {
    onChange(color.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative w-[250px]">
      {/* Selected value display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-8 flex items-center justify-between px-3 py-1 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center gap-2">
          {selectedColor && (
            <span 
              className="w-5 h-5 rounded border border-gray-200"
              style={{ backgroundColor: selectedColor.color }}
            />
          )}
          <span className="text-gray-700">
            {selectedColor ? selectedColor.name : t('portal.colorSelect.placeholder')}
          </span>
        </div>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder={t('portal.colorSelect.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          
          {/* Color options */}
          <div className="max-h-60 overflow-y-auto">
            {filteredColors.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => handleSelect(color)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                  value === color.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <span 
                  className="w-5 h-5 rounded border border-gray-200"
                  style={{ backgroundColor: color.color }}
                />
                {color.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        />
      )}
    </div>
  );
};